import { useState, useContext, useEffect } from "react"
import { Field, FastField, useFormikContext } from "formik"
import { gql, useLazyQuery } from "@apollo/client"
import _ from "lodash"

import { Dropdown } from "primereact/dropdown"
import { FormContext } from "../../../context/formContext"
import { UserContext } from "../../../context/userContext"

import { LoaderSmall } from "../../../components/Loaders"
import styles from "../../../styles/Form.module.css"

export default function SupervisionStaffIdSelector(props) {
  const { type, field, block, index } = props
  const [staffList, setStaffList] = useState([])
  const [loading, setLoading] = useState(true)

  const formik = useFormikContext()
  const form = useContext(FormContext)
  const userCtx = useContext(UserContext)

  /** Safe parse for "required" flag */
  const checkIfRequired = () => {
    try {
      return JSON.parse(field?.field_validation?.required?.[0])
    } catch {
      return false
    }
  }

  const isRequired = checkIfRequired()

  /** GraphQL query */
  const GET_STAFF = gql`
    query getStaffList($staff_id: ID!, $organization_id: ID!) {
      staffsByOrganization: getSupervisionSessionStaff(
        staff_id: $staff_id
        organization_id: $organization_id
      ) {
        staff_id
        staff_name_first
        staff_name_last
      }
    }
  `

  const [getStaff] = useLazyQuery(GET_STAFF)

  /** Fetch staff list on mount */
  useEffect(() => {
    // Register field as loading in FormContext
    form && form.registerLoadingField(field)

    const setOption = async () => {
      try {
        if (!userCtx?.session?.user) {
          console.warn("User context is not ready yet.")
          setLoading(false)
          return
        }

        await getStaff({
          variables: {
            staff_id: userCtx.session.user.staff_id,
            organization_id: userCtx.session.user.organization_id,
          },
          onCompleted: (data) => {
            const { staffsByOrganization } = data || {}

            if (!Array.isArray(staffsByOrganization)) {
              console.warn("Unexpected GraphQL response:", data)
              setStaffList([])
              setLoading(false)
              return
            }

            const options = staffsByOrganization
              .map((staff) => {
                if (!staff) return null
                const { staff_name_first, staff_name_last, staff_id } = staff

                if (!staff_id) return null

                return {
                  label: `${_.unescape(staff_name_first || "")} ${_.unescape(
                    staff_name_last || ""
                  )}`.trim(),
                  key: staff_id,
                }
              })
              .filter(Boolean)

            setStaffList(options)
            setLoading(false)
          },
          onError: (err) => {
            console.error("Error loading staff list:", err)
            setStaffList([])
            setLoading(false)
          },
        })
      } catch (error) {
        console.error("Unexpected failure inside setOption:", error)
        setStaffList([])
        setLoading(false)
      }
    }

    setOption()

    // Notify FormContext that field is loaded
    form && form.completeLoadingField(field)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Fix: Convert raw value → object from staffList */
  const remapValue = (value, options) => {
    if (!value) return null
    const match = options.find((opt) => opt.key === value)

    if (!match) {
      console.warn(
        "[SupervisionStaffIdSelector] Key not found in dropdown options:",
        value,
        "\nAvailable options:",
        options
      )
    }

    return match || null
  }

  /** Disable field if needed */
  const isDisabled = form ? form.blocksSaved?.[block] : false

  /** Handle multi-field mode */
  const handleFieldPerType = () => {
    if (type === "multi-field") {
      const { field, mfFieldName, mfIndex } = props
      const fieldSlug = `${mfFieldName}.${mfIndex}.${field.field_name}`

      return (
        <FastField name={fieldSlug}>
          {({ field: formikField, meta }) => {
            const mappedValue = remapValue(formikField.value, staffList)
            const errorMsg =
              typeof meta?.error?.key === "string" ? meta.error.key : null

            return (
              <>
                <Dropdown
                  placeholder="Please select"
                  id={field?.field_name}
                  name={formikField.name}
                  value={mappedValue}
                  onChange={(e) =>
                    formik.setFieldValue(fieldSlug, e.value?.key || "")
                  }
                  disabled={isDisabled}
                  options={staffList}
                />
                {errorMsg && (
                  <div className={styles.errFieldMsg}>{errorMsg}</div>
                )}
              </>
            )
          }}
        </FastField>
      )
    }

    /** Normal mode */
    return (
      <Field name={field.field_name}>
        {({ field: formikField, meta }) => {
          const mappedValue = remapValue(formikField.value, staffList)

          return (
            <>
              <Dropdown
                placeholder="Please select"
                id={field?.field_name}
                name={formikField.name}
                value={mappedValue}
                onChange={(e) =>
                  formik.setFieldValue(field.field_name, e.value?.key || "")
                }
                disabled={isDisabled}
                options={staffList}
              />

              {meta.touched && meta.error && (
                <div className={styles.errFieldMsg}>{meta.error}</div>
              )}
            </>
          )
        }}
      </Field>
    )
  }

  if (loading) return <LoaderSmall />

  return (
    <div className={styles.field} style={{ order: index }}>
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>

      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}

      <div className={styles.value}>
        {handleFieldPerType()}
      </div>
    </div>
  )
}
